/**
 * Request validation for the developer asset API — the public REST routes under
 * `src/app/api/v1/assets/**` and the hosted MCP tools in `src/app/api/mcp`.
 * Both surfaces call `src/lib/api/asset-service.ts`, which calls these.
 *
 * Pure functions only, so they are unit-tested directly. Pricing is NOT derived
 * here: the `assets_a_normalize_pricing` trigger owns `is_free` and strips
 * fields that don't belong to the pricing type, so we pass `pricing_type` /
 * `price` through and let the database settle the shape.
 */
import type { Database } from "@/integrations/supabase/types";

type AssetInsert = Database["public"]["Tables"]["assets"]["Insert"];
type AssetUpdate = Database["public"]["Tables"]["assets"]["Update"];
type AssetStatus = Database["public"]["Enums"]["asset_status"];
type LicenseType = Database["public"]["Enums"]["license_type"];

export const ASSET_STATUSES = [
  "draft",
  "pending",
  "scheduled",
  "published",
  "rejected",
  "changes_requested",
  "archived",
] as const satisfies readonly AssetStatus[];

/** Statuses a seller can send (back) to review — same set the web editor allows. */
export const RESUBMITTABLE_STATUSES = [
  "draft",
  "rejected",
  "changes_requested",
] as const satisfies readonly AssetStatus[];

export const PRICING_TYPES = ["fixed", "free", "pwyw"] as const;
export const VISIBILITIES = ["public", "unlisted", "private"] as const;
export const LICENSE_TYPES = [
  "no_sharing",
  "sharing_allowed",
  "custom",
  "uefn_map_use",
  "revenue_share",
] as const satisfies readonly LicenseType[];

export const MAX_TAGS = 20;
export const MAX_TITLE_LENGTH = 200;
const SLUG_RE = /^[a-z0-9-]+$/;

export type Parsed<T> = { ok: true; value: T } | { ok: false; error: string };

type Fields = Record<string, unknown>;

function isRecord(v: unknown): v is Fields {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function oneOf<T extends string>(list: readonly T[], v: unknown): v is T {
  return typeof v === "string" && (list as readonly string[]).includes(v);
}

function isNonNegativeNumber(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v) && v >= 0;
}

function isIsoDate(v: unknown): v is string {
  return typeof v === "string" && !Number.isNaN(Date.parse(v));
}

export function isAssetStatus(v: unknown): v is AssetStatus {
  return oneOf(ASSET_STATUSES, v);
}

export function isResubmittableStatus(v: unknown): boolean {
  return oneOf(RESUBMITTABLE_STATUSES, v);
}

/** `tags` must be an array of non-empty strings, at most MAX_TAGS, deduplicated. */
export function parseTags(v: unknown): Parsed<string[]> {
  if (!Array.isArray(v) || v.some((t) => typeof t !== "string")) {
    return { ok: false, error: "tags must be an array of strings." };
  }
  const tags = [...new Set((v as string[]).map((t) => t.trim()).filter(Boolean))];
  if (tags.length > MAX_TAGS) {
    return { ok: false, error: `tags may contain at most ${MAX_TAGS} entries.` };
  }
  return { ok: true, value: tags };
}

/**
 * Validates the editable listing fields shared by create and update. Unknown
 * keys are ignored; a known key with the wrong type is an error, so a caller
 * never gets a silent no-op or a raw CHECK-constraint 500.
 */
function parseEditableFields(body: Fields): Parsed<AssetUpdate> {
  const out: AssetUpdate = {};

  if ("title" in body) {
    const t = body.title;
    if (typeof t !== "string" || t.trim().length < 3 || t.trim().length > MAX_TITLE_LENGTH) {
      return { ok: false, error: `title must be 3–${MAX_TITLE_LENGTH} characters.` };
    }
    out.title = t.trim();
  }
  if ("description" in body) {
    if (body.description !== null && typeof body.description !== "string") {
      return { ok: false, error: "description must be a string or null." };
    }
    out.description = body.description as string | null;
  }
  if ("category_id" in body) {
    if (body.category_id !== null && typeof body.category_id !== "string") {
      return { ok: false, error: "category_id must be a category UUID or null." };
    }
    out.category_id = body.category_id as string | null;
  }
  if ("pricing_type" in body) {
    if (!oneOf(PRICING_TYPES, body.pricing_type)) {
      return { ok: false, error: `pricing_type must be one of: ${PRICING_TYPES.join(", ")}.` };
    }
    out.pricing_type = body.pricing_type;
  }
  if ("price" in body) {
    if (!isNonNegativeNumber(body.price)) {
      return { ok: false, error: "price must be a number >= 0 (USD)." };
    }
    out.price = body.price;
  }
  if ("pwyw_minimum" in body) {
    if (body.pwyw_minimum !== null && !isNonNegativeNumber(body.pwyw_minimum)) {
      return { ok: false, error: "pwyw_minimum must be a number >= 0 or null." };
    }
    out.pwyw_minimum = body.pwyw_minimum as number | null;
  }
  if ("tipping_enabled" in body) {
    if (typeof body.tipping_enabled !== "boolean") {
      return { ok: false, error: "tipping_enabled must be a boolean." };
    }
    out.tipping_enabled = body.tipping_enabled;
  }
  if ("license_type" in body) {
    if (!oneOf(LICENSE_TYPES, body.license_type)) {
      return { ok: false, error: `license_type must be one of: ${LICENSE_TYPES.join(", ")}.` };
    }
    out.license_type = body.license_type;
  }
  if ("custom_license" in body) {
    if (body.custom_license !== null && typeof body.custom_license !== "string") {
      return { ok: false, error: "custom_license must be a string or null." };
    }
    out.custom_license = body.custom_license as string | null;
  }
  if ("video_url" in body) {
    if (body.video_url !== null && typeof body.video_url !== "string") {
      return { ok: false, error: "video_url must be a string or null." };
    }
    out.video_url = body.video_url as string | null;
  }
  if ("visibility" in body) {
    if (!oneOf(VISIBILITIES, body.visibility)) {
      return { ok: false, error: `visibility must be one of: ${VISIBILITIES.join(", ")}.` };
    }
    out.visibility = body.visibility;
  }
  return { ok: true, value: out };
}

export type DraftAssetInput = { insert: AssetInsert; tags: string[] };

/** Body of `POST /api/v1/assets`, one item of `/assets/batch`, or MCP `create_asset`. */
export function parseDraftAssetInput(raw: unknown, sellerId: string): Parsed<DraftAssetInput> {
  if (!isRecord(raw)) return { ok: false, error: "Each asset must be a JSON object." };

  if (typeof raw.title !== "string") {
    return { ok: false, error: `title must be 3–${MAX_TITLE_LENGTH} characters.` };
  }
  if (typeof raw.slug !== "string" || !SLUG_RE.test(raw.slug)) {
    return { ok: false, error: "slug must contain only lowercase letters, numbers, and hyphens." };
  }

  const fields = parseEditableFields(raw);
  if (!fields.ok) return fields;

  if ("version" in raw && typeof raw.version !== "string") {
    return { ok: false, error: "version must be a string." };
  }

  let tags: string[] = [];
  if (raw.tags !== undefined) {
    const parsed = parseTags(raw.tags);
    if (!parsed.ok) return parsed;
    tags = parsed.value;
  }

  const insert: AssetInsert = {
    ...fields.value,
    title: fields.value.title as string,
    seller_id: sellerId,
    slug: raw.slug,
    status: "draft",
    ...(typeof raw.version === "string" ? { version: raw.version } : {}),
  };
  return { ok: true, value: { insert, tags } };
}

export type AssetPatchInput = { patch: AssetUpdate; tags: string[] | undefined };

/**
 * Body of `PATCH /api/v1/assets/:id` or MCP `update_asset`. Adds the fields only
 * an existing listing has: sale window and scheduled publish time. Status,
 * file pointer and version are deliberately absent — they move through submit,
 * confirm-file and staff review.
 */
export function parseAssetPatchInput(raw: unknown): Parsed<AssetPatchInput> {
  if (!isRecord(raw)) return { ok: false, error: "Request body must be a JSON object." };

  const fields = parseEditableFields(raw);
  if (!fields.ok) return fields;
  const patch: AssetUpdate = { ...fields.value };

  if ("sale_price" in raw) {
    if (raw.sale_price !== null && !isNonNegativeNumber(raw.sale_price)) {
      return { ok: false, error: "sale_price must be a number >= 0 or null." };
    }
    patch.sale_price = raw.sale_price as number | null;
  }
  for (const key of ["sale_starts_at", "sale_ends_at", "scheduled_publish_at"] as const) {
    if (key in raw) {
      if (raw[key] !== null && !isIsoDate(raw[key])) {
        return { ok: false, error: `${key} must be an ISO 8601 timestamp or null.` };
      }
      patch[key] = raw[key] as string | null;
    }
  }

  let tags: string[] | undefined;
  if (raw.tags !== undefined) {
    const parsed = parseTags(raw.tags);
    if (!parsed.ok) return parsed;
    tags = parsed.value;
  }

  if (Object.keys(patch).length === 0 && tags === undefined) {
    return { ok: false, error: "No valid fields provided to update." };
  }
  return { ok: true, value: { patch, tags } };
}

/** `file_size` on confirm-file: optional positive integer byte count. */
export function parseFileSize(v: unknown): Parsed<number | undefined> {
  if (v === undefined) return { ok: true, value: undefined };
  if (typeof v !== "number" || !Number.isInteger(v) || v < 1) {
    return { ok: false, error: "file_size must be a positive integer (bytes)." };
  }
  return { ok: true, value: v };
}

/** Storage folder the API issues upload URLs under, in both asset buckets. */
export function assetUploadFolder(profileId: string, assetId: string): string {
  return `${profileId}/${assetId}/`;
}

/**
 * True when `path` is an object this API could have issued for this seller's
 * asset. Confirming any other path would let a caller point a listing at a
 * file that belongs to someone else.
 */
export function isAssetUploadPath(path: unknown, profileId: string, assetId: string): path is string {
  if (typeof path !== "string") return false;
  const folder = assetUploadFolder(profileId, assetId);
  const rest = path.slice(folder.length);
  return path.startsWith(folder) && rest.length > 0 && !rest.includes("/") && !rest.includes("..");
}

/**
 * Inverse of `publicStorageUrl("asset-images", path)`: the object path inside
 * the public asset-images bucket, or null when the URL points anywhere else.
 */
export function assetImagePathFromPublicUrl(url: string, supabaseUrl: string): string | null {
  const prefix = `${supabaseUrl.replace(/\/+$/, "")}/storage/v1/object/public/asset-images/`;
  return url.startsWith(prefix) ? url.slice(prefix.length) : null;
}

/**
 * Status a submitted listing moves to. A future `scheduled_publish_at` parks it
 * as `scheduled`; the promote_scheduled_assets cron moves it to review at that
 * time — the same choice the web editor makes.
 */
export function submittedStatus(
  scheduledPublishAt: string | null,
  now: Date = new Date(),
): "pending" | "scheduled" {
  return scheduledPublishAt && Date.parse(scheduledPublishAt) > now.getTime()
    ? "scheduled"
    : "pending";
}
